// 📄 pages/MiEspacio/sections/ArchivosSection.tsx
import { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Download, FileText, X, Paperclip } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { Archivo, TIPOS_ARCHIVO_ACEPTADOS } from '../../../types/miEspacio.ts';
import { getArchivos, createArchivo, renameArchivo, deleteArchivo } from '../../../services/miEspacioService.ts';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import { MiEspacioSeccionProps, etiquetaContexto } from '../miEspacioContexto.ts';

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

const formatoTamano = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function ArchivosSection({ contextosConsulta, contextoNuevo, proyectosPorId, mostrarProyecto }: MiEspacioSeccionProps) {
  const { user } = useAuth();
  const [archivos, setArchivos] = useState<Archivo[]>([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [archivoElegido, setArchivoElegido] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editando, setEditando] = useState<Archivo | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [editDescripcion, setEditDescripcion] = useState('');
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  const [aEliminar, setAEliminar] = useState<Archivo | null>(null);

  const cargar = async () => {
    if (!user) return;
    setLoading(true);
    try {
      setArchivos(await getArchivos(user.uid, contextosConsulta));
    } catch (err) {
      console.error('Error cargando archivos:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [user?.uid, contextosConsulta.join(',')]);

  const openCreate = () => {
    setNombre('');
    setDescripcion('');
    setArchivoElegido(null);
    setError(null);
    setFormOpen(true);
  };

  const handleSubir = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !archivoElegido) return;
    setSubiendo(true);
    setError(null);
    try {
      await createArchivo(user.uid, nombre, descripcion, archivoElegido, contextoNuevo);
      setFormOpen(false);
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al subir el archivo.');
    } finally {
      setSubiendo(false);
    }
  };

  const openEdit = (archivo: Archivo) => {
    setEditando(archivo);
    setEditNombre(archivo.nombre);
    setEditDescripcion(archivo.descripcion);
  };

  const handleGuardarEdicion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !editando) return;
    setGuardandoEdicion(true);
    try {
      await renameArchivo(user.uid, editando.id, editNombre, editDescripcion, editando.projectId);
      setEditando(null);
      cargar();
    } catch (err) {
      console.error('Error al renombrar archivo:', err);
    } finally {
      setGuardandoEdicion(false);
    }
  };

  const handleEliminar = async () => {
    if (!user || !aEliminar) return;
    try {
      await deleteArchivo(user.uid, aEliminar.id, aEliminar.projectId);
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar archivo:', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {!formOpen && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all">
            <Plus className="w-4 h-4" /> Subir archivo
          </button>
        )}
      </div>

      {formOpen && (
        <form onSubmit={handleSubir} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-400" /> Subir archivo
            </h2>
            <button type="button" onClick={() => setFormOpen(false)} className="text-gray-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Archivo</label>
            <label className="flex items-center gap-2 bg-zinc-800 border border-dashed border-white/20 rounded-lg p-3 text-sm text-gray-300 hover:border-blue-500 cursor-pointer transition-all">
              <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
              {archivoElegido ? archivoElegido.name : 'PDF, Excel, Word, PowerPoint o XML...'}
              <input
                type="file"
                accept={TIPOS_ARCHIVO_ACEPTADOS}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setArchivoElegido(file);
                  if (file && !nombre.trim()) setNombre(file.name);
                }}
              />
            </label>
          </div>

          <div>
            <label className={labelClass}>Nombre</label>
            <input className={inputClass} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: Layout de carga masiva" />
          </div>

          <div>
            <label className={labelClass}>Descripción (opcional)</label>
            <textarea className={`${inputClass} min-h-[70px]`} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={subiendo || !archivoElegido} className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:opacity-50 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all">
              {subiendo ? 'Subiendo...' : 'Subir archivo'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Cargando archivos...</p>
      ) : archivos.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <FileText className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no tenés archivos guardados.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {archivos.map((item) => (
            <div key={item.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <h3 className="text-sm font-semibold text-white truncate">{item.nombre}</h3>
                  {mostrarProyecto && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 flex-shrink-0">
                      {etiquetaContexto(item.projectId, proyectosPorId)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{item.archivo.name} · {formatoTamano(item.archivo.size)}</p>
                {item.descripcion && <p className="text-xs text-gray-400 mt-1.5">{item.descripcion}</p>}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <a
                  href={item.archivo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
                  title="Descargar"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10" title="Renombrar">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => setAEliminar(item)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10" title="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editando && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <form onSubmit={handleGuardarEdicion} className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-white">Renombrar archivo</h3>
              <button type="button" onClick={() => setEditando(null)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500">El archivo en sí ({editando.archivo.name}) no cambia — solo el nombre y la descripción.</p>
            <div>
              <label className={labelClass}>Nombre</label>
              <input className={inputClass} value={editNombre} onChange={(e) => setEditNombre(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Descripción</label>
              <textarea className={`${inputClass} min-h-[70px]`} value={editDescripcion} onChange={(e) => setEditDescripcion(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditando(null)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all">
                Cancelar
              </button>
              <button type="submit" disabled={guardandoEdicion || !editNombre.trim()} className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all">
                {guardandoEdicion ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar este archivo?"
          message={`"${aEliminar.nombre}" se eliminará permanentemente.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
